import { PAGE_NAMES } from '@/const/common';
import { BASE_TONNAGE } from '@/const/license';
import { BEFORE_SALE } from '@/const/productStatus';
import {
  PRODUCT_TYPE_SPEED,
  SALES_TYPE_ASSURANCE,
  SALES_TYPE_CONSIGNMENT,
  SALES_TYPE_NORMAL,
  WIDE_WING_BODY,
  WIDE_WING_BODY_MIN_TONS,
} from '@/const/products';

export const getPageName = (data: ProductDetailResponse): keyof typeof PAGE_NAMES => {
  if (data.status.code === BEFORE_SALE) {
    if (!data.model) return 'model';
    if (!data.tons) return 'tons';
    if (!data.loaded || !data.loadedInnerLength) return 'loaded';
    if (!data.axis) return 'axis';
    if (!data.type) return 'price-trend';
    if (!data.transmission || !data.distance || !data.fuel || !data.power || !data.productsImage)
      return 'additional-info';
    if (
      !data?.productsImage?.frontSideImageUrl &&
      !data?.productsImage?.backSideImageUrl &&
      !data?.productsImage?.frontImageUrl
    )
      return 'additional-info';
    if (
      !data?.productsImage?.frontSideImageUrl ||
      !data?.productsImage?.backSideImageUrl ||
      !data?.productsImage?.frontImageUrl
    )
      return 'photo';
    if (!data.price) return 'price';
  }
  return 'price';
};

/** 광폭윙바디는 4.6톤 이상 차량에서만 선택할 수 있다. (톤수 미입력 시에도 미노출) */
export const getSelectableLoadedList = <T extends EnumPresenter>(loadedList: T[], tons: number): T[] => {
  if (tons >= WIDE_WING_BODY_MIN_TONS) return loadedList;
  return loadedList.filter((item) => item.code !== WIDE_WING_BODY);
};

export const isLengthOnlyLoadedType = (data: any) => {
  const validCodes = ['CARGO', 'TANKLORRY', 'TRAILER', 'LADDER', 'AUTOLADDER', 'TONGS'];
  return validCodes.includes(data?.loaded?.code);
};

export const isAllLoadedInfoAvailable = (data: any) => {
  return !!data?.loadedInnerLength && !!data?.loadedInnerArea && !!data?.loadedInnerHeight;
};

export const shouldShowOnlyLength = (data: any) => {
  return (
    !isAllLoadedInfoAvailable(data) &&
    (isLengthOnlyLoadedType(data) || data?.loaded?.code === '' || data?.loaded === null)
  );
};

export const getProductType = (
  type?: EnumPresenter,
  salesType?: EnumPresenter | null,
  statusOfSpeedProduct?: EnumPresenter,
) => {
  if (type?.code == PRODUCT_TYPE_SPEED) {
    return statusOfSpeedProduct;
  } else {
    return getProductTypeBySalesType(salesType?.code);
  }
};

export const getProductTypeBySalesType = (salesType?: string) => {
  switch (salesType) {
    case SALES_TYPE_NORMAL:
      return { code: salesType, desc: '직거래' };
    case SALES_TYPE_CONSIGNMENT:
      return { code: salesType, desc: '알선판매' };
    case SALES_TYPE_ASSURANCE:
      return { code: salesType, desc: '직트럭 상품용' };
  }
};

export const getSalesTypeDisplayDesc = (salesType?: EnumPresenter | null): string => {
  if (!salesType?.desc) return '';
  return salesType.desc.replace(/위탁판매/g, '알선판매');
};

export const mapProductEnumSalesType = (productEnum: ProductEnum): ProductEnum => ({
  ...productEnum,
  salesType: productEnum.salesType.map((item) => ({
    ...item,
    desc: getSalesTypeDisplayDesc(item),
  })),
});

export const isShowTypeChangeButtonAndMoreMenu = (salesType?: string) => {
  return SALES_TYPE_CONSIGNMENT != salesType;
};

/** 매입견적(SPEED) 상품은 수정 불가 */
export const canModifyProduct = (productsType?: string) => {
  return productsType !== PRODUCT_TYPE_SPEED;
};

/** 알선/상품용 영업사원 전화번호 (안심번호 우선) */
export const getSalesPeopleCallNumber = (salesPeople?: SalesPeople | null) => {
  return salesPeople?.safetyNumber || salesPeople?.phoneNumber || undefined;
};

export const determineLicensePlateDescription = (tons: number) => {
  let maxTons = BASE_TONNAGE;

  if (tons > BASE_TONNAGE) {
    maxTons = 25;
  } else if (tons * 1.5 > BASE_TONNAGE) {
    maxTons = Number((tons * 1.5).toFixed(1));
  }

  return maxTons;
};
